import { apiClient } from "./base.service";
import { auth } from "../firebase/config";
import type {
  Startup,
  CreateStartupData,
  UpdateStartupData,
  StartupMembersResponse,
  PendingInviteSummary,
  InviteMemberResult,
} from "@/types/startup.types";

class StartupService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getAll(): Promise<Startup[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get<Startup[]>("/startups", { headers });
  }

  async getById(id: string): Promise<Startup> {
    const headers = await this.getAuthHeaders();
    return apiClient.get<Startup>(`/startups/${id}`, { headers });
  }

  async create(data: CreateStartupData): Promise<Startup> {
    const headers = await this.getAuthHeaders();
    return apiClient.post<Startup>("/startups", data, { headers });
  }

  async update(id: string, data: UpdateStartupData): Promise<Startup> {
    const headers = await this.getAuthHeaders();
    return apiClient.put<Startup>(`/startups/${id}`, data, { headers });
  }

  async delete(id: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete<void>(`/startups/${id}`, { headers });
  }

  async getMembers(id: string): Promise<StartupMembersResponse> {
    const headers = await this.getAuthHeaders();
    return apiClient.get<StartupMembersResponse>(`/startups/${id}/members`, {
      headers,
    });
  }

  async inviteMember(
    id: string,
    email: string,
    role = "editor",
  ): Promise<InviteMemberResult> {
    const headers = await this.getAuthHeaders();
    return apiClient.post<InviteMemberResult>(
      `/startups/${id}/invites`,
      { email, role },
      { headers },
    );
  }

  async acceptInvite(id: string): Promise<{ member: unknown; startup: Startup }> {
    const headers = await this.getAuthHeaders();
    return apiClient.post<{ member: unknown; startup: Startup }>(
      `/startups/${id}/invites/accept`,
      undefined,
      { headers },
    );
  }

  async removeMember(id: string, memberUserId: string): Promise<{ startup: Startup }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete<{ startup: Startup }>(
      `/startups/${id}/members/${memberUserId}`,
      { headers },
    );
  }

  async getPendingInvites(): Promise<PendingInviteSummary[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get<PendingInviteSummary[]>("/invites/pending", { headers });
  }
}

export const startupService = new StartupService();
